import { useTranslation } from 'react-i18next';

import Earth from '@/assets/icons/earth_line.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { SelectMenu } from '@/components/molecules/SelectMenu';
import { chooseLanguage } from '@/i18n/languageChoice';
import { useI18nInstance } from '@/i18n/useI18nInstance';

import styles from './ChangeLanguage.module.scss';

const LANGUAGES = [
    { code: 'en', label: 'English (US)', displayName: 'English' },
    { code: 'nb', label: 'Norsk (Bokmål)', displayName: 'Norsk (Bokmål)' },
    { code: 'es', label: 'Español', displayName: 'Español' },
] as const;

type LanguageCode = (typeof LANGUAGES)[number]['code'];

export type ChangeLanguageVariant = 'header' | 'mobile';

export interface ChangeLanguageProps {
    variant?: ChangeLanguageVariant;
    /** `inverse` on a surface that is navy in both themes, where the theme's text colour is navy too. */
    surface?: 'default' | 'inverse';
}

export default function ChangeLanguage(props: ChangeLanguageProps) {
    const { variant = 'header', surface = 'default' } = props;
    const { i18n } = useTranslation();
    const instance = useI18nInstance();

    const direction = variant === 'mobile' ? 'up' : 'down';

    const changeLanguage = (language: LanguageCode) => chooseLanguage(instance, language);

    // The language the text is in, not the code asked for: an unsupported or regional code
    // resolves to one of these, and the picker has to name that one.
    const shownLanguage = i18n.resolvedLanguage ?? i18n.language;
    const currentLanguage = LANGUAGES.find((lang) => lang.code === shownLanguage) ?? LANGUAGES[0];

    return (
        <div className={styles.root} data-variant={variant} data-surface={surface}>
            <SelectMenu
                value={currentLanguage.code}
                onChange={changeLanguage}
                options={LANGUAGES.map((lang) => ({ value: lang.code, label: lang.label }))}
                renderValue={() => (
                    <span className={styles.selectedLanguage}>
                        <SvgIcon
                            icon={Earth}
                            size="inherit"
                            className={styles.globeIcon}
                            svgProps={{ 'aria-hidden': true }}
                        />
                        <span>{currentLanguage.displayName}</span>
                    </span>
                )}
                caret={false}
                direction={direction}
                variant={variant === 'header' ? 'unstyled' : 'sidebar'}
                triggerClassName={variant === 'header' ? styles.toggleHeader : undefined}
                align="end"
                appearance={variant === 'mobile' ? 'sidebar' : 'default'}
                width="wide"
            />
        </div>
    );
}
